import { Pagination, SuccessResponse } from "common/common.interface";
import { useAppSelector, useSnack } from "common/hooks";
import { useInfiniteQueryWithLoading } from "common/hooks/src/api/useInfiniteQueryWithLoading";
import { useMutationWithLoading } from "common/hooks/src/api/useMutationWithLoading";
import { useQueryWithLoading } from "common/hooks/src/api/useQueryWithLoading";
import {
  BonDeCommande,
  CreateBonDeCommandePayload,
} from "modules/bonDeCommande/types/bonDeCommande.interface";
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useQueryClient,
} from "react-query";
import { useNavigate } from "react-router-dom";
import { BON_COMMANDES_ROUTES } from "./bonDeCommande.api.enum";
import { getBonDeCommandeBackend } from "./backends";
import { DevisFilter } from "modules/devis/types/devis.interface";

export const useGetBonDeCommandes = (
  searchName: string | undefined,
  filters?: DevisFilter
): UseInfiniteQueryResult<Pagination<BonDeCommande>> => {
  const setSnack = useSnack();

  const getAllBonDeCommandes = async ({
    pageParam = 1,
  }): Promise<Pagination<BonDeCommande>> => {
    const bonDeCommandeBackend = await getBonDeCommandeBackend();
    console.log("pageParam", pageParam);
    return await bonDeCommandeBackend.getBonDeCommandes(
      pageParam,
      10,
      searchName,
      filters
    );
  };

  return useInfiniteQueryWithLoading(
    BON_COMMANDES_ROUTES.GET_ALL + searchName,
    getAllBonDeCommandes,
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useGetBonDeCommande = (
  id: string | undefined
): UseQueryResult<BonDeCommande> => {
  const setSnack = useSnack();

  const getBonDeCommande = async (): Promise<BonDeCommande> => {
    const bonDeCommandeBackend = await getBonDeCommandeBackend();
    return await bonDeCommandeBackend.getBonDeCommande(id);
  };

  return useQueryWithLoading(
    BON_COMMANDES_ROUTES.GET + id,
    () => getBonDeCommande(),
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
      enabled: !!id,
    }
  );
};

export const useCreateBonDeCommande = (
  commandeId: string | undefined
): UseMutationResult<BonDeCommande, Error, CreateBonDeCommandePayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.user);

  const createBonDeCommande = async (
    payload: CreateBonDeCommandePayload
  ): Promise<BonDeCommande> => {
    const bonDeCommandeBackend = await getBonDeCommandeBackend();
    return await bonDeCommandeBackend.createBonDeCommande(
      user.workspaceId,
      payload,
      commandeId
    );
  };

  return useMutationWithLoading(
    (payload: CreateBonDeCommandePayload) => createBonDeCommande(payload),
    {
      onSuccess() {
        setSnack({
          title: "Bon de commande créé avec succès!",
          severityType: "success",
        });

        queryClient.refetchQueries(BON_COMMANDES_ROUTES.GET_ALL);

        navigate("/admin/bon-commande");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useUpdateBonDeCommande = (
  id: string | undefined,
  updateLate?: boolean
): UseMutationResult<BonDeCommande, Error, CreateBonDeCommandePayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const updateBonDeCommande = async (
    payload: CreateBonDeCommandePayload
  ): Promise<BonDeCommande> => {
    const bonDeCommandeBackend = await getBonDeCommandeBackend();
    return await bonDeCommandeBackend.updateBonDeCommande(
      id ?? payload._id,
      payload
    );
  };

  return useMutationWithLoading(
    (payload: CreateBonDeCommandePayload) => updateBonDeCommande(payload),
    {
      onSuccess: (data) => {
        if (!updateLate) {
          setSnack({
            title: "Bon de commande modifié avec succès!",
            severityType: "success",
          });

          queryClient.invalidateQueries([BON_COMMANDES_ROUTES.GET, id]);
          queryClient.setQueryData([BON_COMMANDES_ROUTES.GET, id], data);

          navigate("/admin/bon-commande");
        } else {
          setSnack({
            title: "Contact enregistré avec succès!",
            severityType: "success",
          });

          queryClient.invalidateQueries([BON_COMMANDES_ROUTES.GET_LATE]);
        }
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useArchiveBonDeCommande = (): UseMutationResult<
  SuccessResponse,
  Error,
  string
> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const archiveBonDeCommande = async (id: string): Promise<SuccessResponse> => {
    const bonDeCommandeBackend = await getBonDeCommandeBackend();
    return await bonDeCommandeBackend.archiveBonDeCommande(id);
  };

  return useMutationWithLoading((id: string) => archiveBonDeCommande(id), {
    onSuccess() {
      setSnack({
        title: "Bon de commande archivé avec succès!",
        severityType: "success",
      });

      queryClient.refetchQueries(BON_COMMANDES_ROUTES.GET_ALL);

      navigate("/admin/bon-commande");
    },
    onError() {
      setSnack({
        title: "Erreur lors de l'archivage du bon de commande",
        severityType: "error",
      });
    },
  });
};
